import React, { useState, useCallback, useEffect } from "react";
import i18next from "i18next";
import useLoanCalculation from "../Hooks/useLoan";
import Modal from "../../../StyledComponents/Modal";
import {
  ModalFoldout,
  ModalFoldoutBody,
} from "../../../StyledComponents/Modal/wrapper";
import Content from "../../../StyledComponents/Content";
import LogoBox from "../../../StyledComponents/LogoBox";
import {
  Repeat,
  RepeatSmall,
  RepeatTiny,
} from "../../../StyledComponents/Repeat";
import { numberSeparator } from "../../../utils/formats";
import { ButtonClear, ButtonContent } from "../../../StyledComponents/Button";
import {
  ContentLogo,
  ContentLogoText,
  ContentLogoMedia,
} from "../../../StyledComponents/ContentLogo";
import DataList from "../../../StyledComponents/DataList";
import { FinancialOption, Query } from "../../../@types/codegen/types";
import PubSub from "../../../utils/PubsubItem";
import SliderWithLabel from "../../../StyledComponents/RangeSlider/SliderWithLabel";
import { getMarketCode } from "../../../utils/market";
import { CreditorDisclaimer } from "../../../StyledComponents/CreditorDisclaimer";

const stepGenerator = (
  step: number,
  max: number,
  steps: number[] = [],
  current?: number
): number[] => {
  if (step === 0 && current === undefined) return steps;

  if (current !== undefined && current >= max && step !== 0) {
    return steps;
  }

  const nextCurrent = current ? step + current : step;
  steps.push(nextCurrent);
  return stepGenerator(step, max, steps, nextCurrent);
};

interface LoanModalProps {
  id: string;
  financialOption: FinancialOption;
  onClose: () => void;
}

const LoanModal = ({ id, financialOption, onClose }: LoanModalProps) => {
  const marketCode = getMarketCode();

  const [tmp, setTmp] = useState<Query>();
  const [extend, setExtend] = React.useState(false);
  const onToggleExtend = React.useCallback(() => {
    if (!extend) {
      PubSub.publish("FinanceInterest");
    }
    setExtend(!extend);
  }, [extend]);

  const [variables, setVariables] = useState({
    duration: financialOption.duration?.current,
    downPayment: financialOption.downPayment?.current,
    residual: financialOption.residual?.current,
  });

  const { duration, downPayment, residual } = variables;
  const { loading, data } = useLoanCalculation(
    id,
    duration,
    downPayment,
    residual,
    financialOption.identifier
  );
  useEffect(() => {
    if (data) {
      setTmp(data);
      setVariables({
        duration: data?.loan?.duration?.current || 0,
        downPayment: data?.loan?.downPayment?.current || 0,
        residual: data?.loan?.residual?.current || 0,
      });
    }
  }, [data]);

  const loan = data?.loan || tmp?.loan;

  const downPaymentCurrent = loan?.downPayment?.current || 0;
  const downPaymentMin = loan?.downPayment?.min || 0;
  const downPaymentMax = loan?.downPayment?.max || 0;
  const downPaymentStep = loan?.downPayment?.step || 0;
  const downPaymentSteps = stepGenerator(downPaymentStep, downPaymentMax);
  const onDownPaymentChange = useCallback(
    (values: readonly number[]) => {
      PubSub.publish("FinanceInterest");
      setVariables({ ...variables, downPayment: values[0] });
    },
    [variables]
  );

  const durationCurrent = loan?.duration?.current || 0;
  const durationMin = loan?.duration?.min || 0;
  const durationMax = loan?.duration?.max || 0;
  const durationStep = loan?.duration?.step || 0;
  const durationSteps = stepGenerator(durationStep, durationMax);
  const onDurationChange = useCallback(
    (values: readonly number[]) => {
      PubSub.publish("FinanceInterest");
      setVariables({ ...variables, duration: values[0] });
    },
    [variables]
  );

  const residualCurrent = (loan?.residual?.current || 0) * 100 || 0;
  const residualMin = (loan?.residual?.min || 0) * 100 || 0;
  const residualMax = (loan?.residual?.max || 0) * 100 || 0;
  const residualStep = (loan?.residual?.step || 0) * 100 || 0;
  const residualSteps = stepGenerator(residualStep, residualMax);
  const onResidualChange = useCallback(
    (values: readonly number[]) => {
      PubSub.publish("FinanceInterest");
      setVariables({ ...variables, residual: values[0] / 100 });
    },
    [variables]
  );

  const interestText = ((loan?.interest || 0) * 100).toFixed(2);
  const effectiveInterestText = ((loan?.effectiveInterest || 0) * 100).toFixed(
    2
  );
  const monthlyCost = loan?.monthlyCost;

  return (
    <Modal
      title={i18next.t("item.financialOptions.loanOptions")}
      onClose={onClose}
    >
      {financialOption?.description ? (
        <Repeat>
          <ContentLogo>
            <ContentLogoText>
              <Content>
                <p>{financialOption.description}</p>
              </Content>
            </ContentLogoText>
            {financialOption?.image && (
              <ContentLogoMedia>
                <LogoBox
                  logo={financialOption.image}
                  alt={
                    financialOption.name || i18next.t("common.logotype") || ""
                  }
                  wide
                />
              </ContentLogoMedia>
            )}
          </ContentLogo>
        </Repeat>
      ) : (
        <>
          {financialOption?.image && (
            <Repeat>
              <LogoBox
                logo={financialOption.image}
                alt={financialOption.name || i18next.t("common.logotype") || ""}
                wide
              />
            </Repeat>
          )}
        </>
      )}

      <Repeat>
        <RepeatSmall>
          <SliderWithLabel
            label={i18next.t("item.financialOptions.downPayment")}
            values={[downPaymentCurrent]}
            domain={[downPaymentMin, downPaymentMax]}
            steps={downPaymentSteps}
            unit={i18next.t("currency.default")}
            formatValues
            loading={loading}
            onChange={onDownPaymentChange}
          />
        </RepeatSmall>
        <RepeatSmall>
          <SliderWithLabel
            label={i18next.t("item.financialOptions.duration")}
            values={[durationCurrent]}
            domain={[durationMin, durationMax]}
            steps={durationSteps}
            unit={i18next.t("common.monthUnitShort")}
            loading={loading}
            onChange={onDurationChange}
          />
        </RepeatSmall>
        {residual !== undefined && residualMax !== 0 && (
          <RepeatSmall>
            <SliderWithLabel
              label={i18next.t("item.financialOptions.residual")}
              values={[residualCurrent]}
              domain={[residualMin, residualMax]}
              steps={residualSteps}
              unit="%"
              loading={loading}
              onChange={onResidualChange}
            />
          </RepeatSmall>
        )}
        <RepeatSmall>
          <RepeatTiny>
            {monthlyCost !== null && monthlyCost !== undefined && (
              <Content>{`${numberSeparator(monthlyCost)} ${i18next.t(
                "currency.monthly"
              )}*`}</Content>
            )}
            <Content small>
              <p>
                *
                {loan?.mileage !== null
                  ? i18next.t(
                      "item.financialOptions.loanOptionsDisclaimerMileage",
                      {
                        interest: interestText,
                        effectiveInterest: effectiveInterestText,
                        mileage: loan?.mileage,
                      }
                    )
                  : i18next.t("item.financialOptions.loanOptionsDisclaimer", {
                      interest: interestText,
                      effectiveInterest: effectiveInterestText,
                    })}
                ;
              </p>
            </Content>
          </RepeatTiny>
        </RepeatSmall>

        <RepeatSmall>
          <ButtonClear
            onClick={onToggleExtend}
            title={
              extend
                ? i18next.t("common.showLessInformation") || ""
                : i18next.t("common.showMoreInformation") || ""
            }
          >
            <ButtonContent>
              {extend
                ? i18next.t("common.lessInformation")
                : i18next.t("common.showMoreInformation")}
            </ButtonContent>
          </ButtonClear>
        </RepeatSmall>
        <>
          {extend && (
            <ModalFoldout>
              <ModalFoldoutBody>
                <DataList
                  items={[
                    {
                      label: i18next.t("item.financialOptions.interest"),
                      value: `${interestText} %`,
                    },
                    {
                      label: i18next.t(
                        "item.financialOptions.effectiveinterest"
                      ),
                      value: `${effectiveInterestText} %`,
                    },
                    {
                      label: i18next.t("item.financialOptions.setupFee"),
                      value: `${numberSeparator(
                        financialOption?.setupFee || 0
                      )} ${i18next.t("currency.default")}`,
                    },
                    {
                      label: i18next.t(
                        "item.financialOptions.administrationFee"
                      ),
                      value: `${numberSeparator(
                        financialOption?.administrationFee || 0
                      )} ${i18next.t("currency.monthly")}`,
                    },
                    {
                      label: i18next.t("item.financialOptions.totalCreditCost"),
                      value: `${numberSeparator(
                        loan?.totalCreditCost || 0
                      )} ${i18next.t("currency.default")}`,
                    },
                  ]}
                />
              </ModalFoldoutBody>
            </ModalFoldout>
          )}
        </>
      </Repeat>
      {marketCode === "SE" && (
        <Repeat>
          <CreditorDisclaimer />
        </Repeat>
      )}
    </Modal>
  );
};

export default LoanModal;
